import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Champ d'adresse avec autocomplétion via l'API Adresse (BAN, gouv.fr — gratuit, sans clé).
 * Les résultats sont priorisés autour de l'Île-de-France.
 *
 * Props:
 *  - value, onChange(text)       : valeur texte du champ (contrôlé)
 *  - onSelect(feature|null)      : appelé quand une suggestion est choisie (ou le champ vidé)
 *      feature = { label, lat, lng, city, postcode, context }
 *  - placeholder, inputClassName, inputStyle, leftIcon, rightSlot, testid
 */
export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder,
  inputClassName,
  inputStyle,
  leftIcon,
  rightSlot,
  testid,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);
  const blurTimer = useRef(null);

  const search = useCallback((q) => {
    if (!q || q.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(
      q
    )}&limit=5&autocomplete=1&lat=48.8566&lon=2.3522`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        setResults(j.features || []);
        setOpen((j.features || []).length > 0);
        setHighlight(-1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Debounce sur la saisie
  useEffect(() => {
    const t = setTimeout(() => search(value), 250);
    return () => clearTimeout(t);
  }, [value, search]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const choose = (f) => {
    const label = f.properties.label;
    onChange?.(label);
    onSelect?.({
      label,
      lat: f.geometry.coordinates[1],
      lng: f.geometry.coordinates[0],
      city: f.properties.city,
      postcode: f.properties.postcode,
      context: f.properties.context,
    });
    setOpen(false);
    setResults([]);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      choose(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      {leftIcon}
      <input
        className={inputClassName}
        style={inputStyle}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(e) => {
          onChange?.(e.target.value);
          if (!e.target.value) onSelect?.(null);
        }}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
        data-testid={testid}
      />
      {rightSlot}
      {open && results.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-surface-200 rounded-xl shadow-lg max-h-64 overflow-auto"
          data-testid={testid ? `${testid}-list` : undefined}
        >
          {results.map((f, i) => (
            <li
              key={f.properties.id || i}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(f);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-4 py-2.5 cursor-pointer text-sm flex flex-col ${
                i === highlight ? "bg-brand-50 text-brand-700" : "text-txt-900 hover:bg-surface-50"
              }`}
            >
              <span className="font-medium truncate">{f.properties.name}</span>
              <span className="text-xs text-txt-500 truncate">
                {f.properties.postcode} {f.properties.city}
              </span>
            </li>
          ))}
        </ul>
      )}
      {loading && value.trim().length >= 3 && !open && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-txt-300">…</span>
      )}
    </div>
  );
}
